<template>
  <li v-show="isMenuItemVisible" class="sidebar-item">
    <div v-if="hasSubMenu" class="secondary-menu--wrap">
      <button
        type="button"
        class="secondary-menu--header fs-small"
        :aria-expanded="isExpanded.toString()"
        :aria-controls="submenuId"
        @click="toggleExpanded"
      >
        <span>{{ $t(`SIDEBAR.${menuItem.label}`) }}</span>
        <fluent-icon
          class="secondary-menu--chevron"
          :icon="isExpanded ? 'chevron-down' : 'chevron-right'"
          size="12"
        />
      </button>
      <div v-if="menuItem.showNewButton" class="submenu-icons">
        <woot-button
          size="tiny"
          variant="clear"
          color-scheme="secondary"
          icon="add"
          class="submenu-icon"
          @click="onClickOpen"
        />
      </div>
    </div>
    <router-link
      v-else
      class="secondary-menu--title secondary-menu--link fs-small"
      :class="computedClass"
      :to="menuItem && menuItem.toState"
    >
      <i
        v-if="menuItem.icon && menuItem.icon.startsWith('fi ')"
        :class="menuItem.icon"
        class="secondary-menu--icon secondary-menu--icon-font"
      />
      <fluent-icon
        v-else
        :icon="menuItem.icon"
        class="secondary-menu--icon"
        size="14"
      />
      {{ $t(`SIDEBAR.${menuItem.label}`) }}
      <span v-if="showChildCount(menuItem.count)" class="count-view">
        {{ `${menuItem.count}` }}
      </span>
      <span
        v-if="menuItem.beta"
        data-view-component="true"
        label="Beta"
        class="beta"
      >
        {{ $t('SIDEBAR.BETA') }}
      </span>
    </router-link>

    <ul
      v-if="hasSubMenu"
      v-show="isExpanded"
      :id="submenuId"
      class="nested vertical menu secondary-menu--submenu"
    >
      <secondary-child-nav-item
        v-for="child in menuItem.children"
        :key="child.id"
        :to="child.toState"
        :label="child.label"
        :label-color="child.color"
        :should-truncate="child.truncateLabel"
        :icon="computedInboxClass(child)"
        :warning-icon="computedInboxErrorClass(child)"
        :show-child-count="showChildCount(child.count)"
        :child-item-count="child.count"
        :is-whatsapp="child.isWhatsapp"
        :account-id="child.account_id"
        :account-name="child.account_name"
        :inbox-connected="child.connected"
        :show-account-label="showAccountLabel"
      />
      <router-link
        v-if="showItem(menuItem)"
        v-slot="{ href, isActive, navigate }"
        :to="menuItem.toState"
        custom
      >
        <li class="menu-item--new">
          <a
            :href="href"
            class="button small link clear secondary"
            :class="{ 'is-active': isActive }"
            @click="e => newLinkClick(e, navigate)"
          >
            <fluent-icon icon="add" size="16" />
            <span class="button__content">
              {{ $t(`SIDEBAR.${menuItem.newLinkTag}`) }}
            </span>
          </a>
        </li>
      </router-link>
    </ul>
  </li>
</template>

<script>
import { mapGetters } from 'vuex';

import adminMixin from '../../../mixins/isAdmin';
import {
  getInboxClassByType,
  getInboxWarningIconClass,
} from 'dashboard/helper/inbox';

import SecondaryChildNavItem from './SecondaryChildNavItem';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../../../store/modules/conversations/helpers/actionHelpers';

export default {
  components: { SecondaryChildNavItem },
  mixins: [adminMixin],
  props: {
    menuItem: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isExpanded: true,
    };
  },
  computed: {
    ...mapGetters({
      activeInbox: 'getSelectedInbox',
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      globalConfig: 'globalConfig/get',
      inboxes: 'inboxes/getInboxes',
      getAccount: 'accounts/getAccount',
    }),
    hasSubMenu() {
      return !!this.menuItem.children;
    },
    submenuId() {
      return `sidebar-section-${this.menuItem.key || this.menuItem.label}`;
    },
    isMenuItemVisible() {
      if (
        this.menuItem.globalConfigFlag &&
        this.menuItem.toStateName !== 'auditlogs_list' &&
        this.menuItem.toStateName !== 'crm_settings'
      ) {
        return !!this.globalConfig[this.menuItem.globalConfigFlag];
      }

      if (
        this.menuItem.featureFlag &&
        this.menuItem.toStateName !== 'auditlogs_list' &&
        this.menuItem.toStateName !== 'crm_settings'
      ) {
        return this.isFeatureEnabledonAccount(
          this.accountId,
          this.menuItem.featureFlag
        );
      }
      return true;
    },
    isAllConversations() {
      return (
        this.$store.state.route.name === 'inbox_conversation' &&
        this.menuItem.toStateName === 'home'
      );
    },
    isMentions() {
      return (
        isOnMentionsView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_mentions'
      );
    },
    isUnattended() {
      return (
        isOnUnattendedView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_unattended'
      );
    },
    isTeamsSettings() {
      return (
        this.$store.state.route.name === 'settings_teams_edit' &&
        this.menuItem.toStateName === 'settings_teams_list'
      );
    },
    isInboxSettings() {
      return (
        this.$store.state.route.name === 'settings_inbox_show' &&
        this.menuItem.toStateName === 'settings_inbox_list'
      );
    },
    isIntegrationsSettings() {
      return (
        this.$store.state.route.name === 'settings_integrations_webhook' &&
        this.menuItem.toStateName === 'settings_integrations'
      );
    },
    isApplicationsSettings() {
      return (
        this.$store.state.route.name === 'settings_applications_integration' &&
        this.menuItem.toStateName === 'settings_applications'
      );
    },
    isCurrentRoute() {
      return this.$store.state.route.name === this.menuItem.toStateName;
    },
    computedClass() {
      // If active inbox is present, do not highlight conversations
      if (this.activeInbox) return ' ';
      if (
        this.isAllConversations ||
        this.isMentions ||
        this.isUnattended ||
        this.isCurrentRoute
      ) {
        return 'is-active';
      }
      if (this.hasSubMenu) {
        if (
          this.isTeamsSettings ||
          this.isInboxSettings ||
          this.isIntegrationsSettings ||
          this.isApplicationsSettings
        ) {
          return 'is-active';
        }
        return ' ';
      }
      return '';
    },
    showAccountLabel() {
      const account = this.getAccount(this.accountId);
      return (
        !!account?.associate_accounts?.length &&
        this.menuItem?.label === 'INBOXES'
      );
    },
  },
  methods: {
    computedInboxClass(child) {
      const { type, phoneNumber, isWhatsapp } = child;
      if (!type) return '';
      const classByType = getInboxClassByType(type, phoneNumber, isWhatsapp);
      return classByType;
    },
    computedInboxErrorClass(child) {
      const { type, reauthorizationRequired } = child;
      if (!type) return '';
      const warningClass = getInboxWarningIconClass(
        type,
        reauthorizationRequired
      );
      return warningClass;
    },
    newLinkClick(e, navigate) {
      if (this.menuItem.newLinkRouteName) {
        navigate(e);
      } else if (this.menuItem.showModalForNewItem) {
        if (this.menuItem.modalName === 'AddLabel') {
          e.preventDefault();
          this.$emit('add-label');
        }
      }
    },
    showItem(item) {
      return this.isAdmin && !!item.newLink;
    },
    onClickOpen() {
      this.$emit('open');
    },
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
    },
    showChildCount(count) {
      return Number.isInteger(count);
    },
  },
};
</script>
<style lang="scss" scoped>
.sidebar-item {
  margin: var(--space-smaller) 0 0;
}

.secondary-menu--wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--space-small);
}

.secondary-menu--header {
  align-items: center;
  appearance: none;
  background: transparent;
  border: none;
  box-shadow: none;
  color: var(--s-500);
  cursor: pointer;
  display: flex;
  flex: 1;
  font-family: inherit;
  font-size: var(--font-size-micro);
  font-weight: var(--font-weight-bold);
  justify-content: space-between;
  letter-spacing: 0.5px;
  line-height: var(--space-normal);
  margin: var(--space-normal) 0 var(--space-micro);
  padding: 0 var(--space-small);
  text-align: left;
  text-transform: uppercase;
  user-select: none;
  width: 100%;

  &:hover {
    color: var(--s-700);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    border-radius: var(--border-radius-small);
    outline: 2px solid #0a7581;
    outline-offset: 2px;
  }
}

.secondary-menu--chevron {
  color: var(--s-400);
  flex-shrink: 0;
  margin-left: var(--space-small);
}

.secondary-menu--submenu {
  display: block;
  max-height: 16rem;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track,
  &::-webkit-scrollbar-button {
    display: none;
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--s-200);
    border-radius: 9999px;

    &:hover {
      background: var(--s-300);
    }
  }

  @supports (-moz-appearance: none) {
    scrollbar-width: thin;
    scrollbar-color: var(--s-200) transparent;
  }
}
.secondary-menu--title {
  color: var(--s-600);
  display: flex;
  font-weight: var(--font-weight-medium);
  line-height: var(--space-normal);
  margin: var(--space-small) 0;
  padding: 0 var(--space-small);
}

.secondary-menu--link {
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
  margin: var(--space-micro) 0;
  padding: var(--space-smaller) var(--space-small);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  border-radius: var(--border-radius-normal);
  color: var(--s-700);
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;

  &:hover {
    background: var(--s-50);
    color: var(--s-800);
  }

  &:focus {
    border-color: var(--color-woot-darker);
  }

  // Ativo no padrão do protótipo: tint ciano + texto ciano-escuro
  &.router-link-exact-active,
  &.is-active {
    background: var(--color-woot-tint);
    color: var(--color-woot-darker);
    font-weight: var(--font-weight-bold);
  }

  &.is-active .count-view {
    background: transparent;
    color: var(--color-woot-darker);
  }
}

.secondary-menu--icon {
  margin-right: 0;
  min-width: var(--space-normal);
}

.secondary-menu--icon-font {
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sub-menu-link {
  color: var(--s-600);
}

.wrap {
  display: flex;
  align-items: center;
}

.label-color--display {
  border-radius: var(--space-smaller);
  height: var(--space-normal);
  margin-right: var(--space-small);
  min-width: var(--space-normal);
  width: var(--space-normal);
}

.inbox-icon {
  position: relative;
  top: -1px;
}

.sidebar-item .menu-item--new {
  padding: var(--space-small) 0;

  .button {
    display: inline-flex;
    color: var(--s-500);
  }
}

.beta {
  padding-right: var(--space-smaller) !important;
  padding-left: var(--space-smaller) !important;
  margin: 0 var(--space-smaller) !important;
  display: inline-block;
  font-size: var(--font-size-micro);
  font-weight: var(--font-weight-medium);
  line-height: 14px;
  border: 1px solid transparent;
  border-radius: 2em;
  color: var(--g-800);
  border-color: var(--g-700);
}

// Contador discreto (texto), igual ao .n-count do protótipo
.count-view {
  align-items: center;
  background: transparent;
  color: var(--s-400);
  display: inline-flex;
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-bold);
  justify-content: center;
  margin-left: auto;
  padding: 0 var(--space-micro);
}

.submenu-icons {
  display: flex;
  align-items: center;

  .submenu-icon {
    padding: 0;
    margin-left: var(--space-small);
  }
}
</style>
